import {useCallback, useEffect, useLayoutEffect, useRef} from 'react';

import {useWBAgentContextStore} from './WBAgentContextStore';

const BOTTOM_OFFSET = 80;
const RIGHT_OFFSET = 24;

/**
 * Keeps the WBAgent chat window positioned inside the available viewport:
 * - Picks a default bottom-right position on first mount if none is saved.
 * - Re-clamps the saved position whenever the container/viewport shrinks,
 *   so the window never drifts off-screen.
 *
 * Interactive drag bounds are handled by `Rnd`'s `bounds="parent"`;
 * this hook only manages initial placement and passive reflow.
 *
 * @param width  Chat window width in pixels.
 * @param height Chat window height in pixels.
 * @returns `containerRef` — attach to the element that defines the placement bounds
 */

export function useBoundedPlacement(width: number, height: number) {
  const containerRef = useRef<HTMLDivElement>(null);
  const setPosition = useWBAgentContextStore(s => s.setWindowPosition);

  const clampToContainer = useCallback(() => {
    const pos = useWBAgentContextStore.getState().windowPosition;
    if (!containerRef.current || !pos) {
      return;
    }
    const {clientWidth: cw, clientHeight: ch} = containerRef.current;

    const clamped = {
      x: Math.max(0, Math.min(pos.x, cw - width)),
      y: Math.max(0, Math.min(pos.y, ch - height)),
    };
    if (clamped.x !== pos.x || clamped.y !== pos.y) {
      setPosition(clamped);
    }
  }, [setPosition, width, height]);

  useLayoutEffect(() => {
    if (!containerRef.current) {
      return;
    }
    const pos = useWBAgentContextStore.getState().windowPosition;
    if (pos != null) {
      clampToContainer();
      return;
    }
    const {clientWidth: cw, clientHeight: ch} = containerRef.current;

    setPosition({
      x: Math.max(0, cw - RIGHT_OFFSET - width),
      y: Math.max(0, ch - BOTTOM_OFFSET - height),
    });
    // Disabling eslint here is justified here because otherwise position would have to be in this dep array,
    // and we don't want to trigger a rerender every time position changes.
  }, []); // oxlint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let rafId: number;
    const scheduleClamp = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => clampToContainer());
    };

    window.addEventListener('resize', scheduleClamp);

    const ro = new ResizeObserver(scheduleClamp);
    if (containerRef.current) {
      ro.observe(containerRef.current);
    }

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', scheduleClamp);
      ro.disconnect();
    };
  }, [clampToContainer]);

  return {
    containerRef,
  };
}
